  if (!customElements.get('td-card-buy-box')) {
    customElements.define(
      'td-card-buy-box',
      class TdCardBuyBox extends HTMLElement {
        connectedCallback() {
          this.initializeElementRefs();
          this.initializeData();
          this.bindInteractionHandlers();
          this.refreshATCElements();
          this.attachATCListener();

          this.render();
          this.bootstrapAsyncATCBindings();
        }

        initializeElementRefs() {
          this.packageButtons = Array.from(this.querySelectorAll('[data-package]'));
          this.purchaseButtons = Array.from(this.querySelectorAll('[data-purchase-type]'));
          this.packagePriceEls = this.querySelectorAll('[data-package-price]');
          this.packageCompareEls = this.querySelectorAll('[data-package-compare]');
          this.onetimePriceEl = this.querySelector('[data-frequency-onetime-price]');
          this.onetimeCompareEl = this.querySelector('[data-frequency-onetime-compare]');
          this.subscriptionPriceEl = this.querySelector('[data-frequency-subscription-price]');
          this.subscriptionCompareEl = this.querySelector('[data-frequency-subscription-compare]');
          this.savingsBadgeEl = this.querySelector('[data-frequency-savings-badge]');
          this.onetimeBadgeEl = this.querySelector('[data-frequency-onetime-badge]');
          this.subscriptionWrap = this.querySelector('[data-subscription-wrap]');
          this.subscriptionDetails = this.querySelector('[data-subscription-details]');
          this.bundleSubscriptionTextEl = this.querySelector('[data-bundle-subscription-text]');
          this.bundleDetailsEl = this.querySelector('[data-bundle-details]');
          this.planSelect = this.querySelector('[data-selling-plan-select]');
          this.sectionRoot = this.closest('.td-lp-combined-product');
          this.sectionId = this.sectionRoot ? this.sectionRoot.dataset.sectionId : null;
          this.atcScope = null;
          this.form = null;
          this.submitButton = null;
          this.atcPriceEl = null;
          this.atcCompareEl = null;
          this.errorWrapper = null;
          this.errorMessage = null;
          this.atcObserver = null;
          this.atcSubmitHandler = (event) => this.handleSubmit(event);
        }

        initializeData() {
          this.mainPlans = this.readJsonData('[data-main-selling-plans]');
          this.bundlePlans = this.readJsonData('[data-bundle-selling-plans]');
          this.currencyFormatter = this.resolveMoneyFormatter();
          this.state = {
            selectedPackage: 'single',
            purchaseType: this.dataset.defaultPurchaseType === 'subscription' ? 'subscription' : 'onetime',
            selectedPlanId: this.planSelect ? String(this.planSelect.value || '') : '',
          };
        }

        bindInteractionHandlers() {
          this.packageButtons.forEach((button) => {
            button.addEventListener('click', () => this.handlePackageChange(button));
          });
          this.purchaseButtons.forEach((button) => {
            button.addEventListener('click', () => this.handlePurchaseTypeChange(button));
          });
          if (this.planSelect) {
            this.planSelect.addEventListener('change', () => {
              this.state.selectedPlanId = String(this.planSelect.value || '');
              this.render();
            });
          }
        }

        bootstrapAsyncATCBindings() {
          setTimeout(() => {
            if (this.form) return;
            this.refreshATCElements();
            this.attachATCListener();
            if (this.form) {
              this.render();
              return;
            }

            if (this.sectionRoot && !this.atcObserver) {
              this.atcObserver = new MutationObserver(() => {
                this.refreshATCElements();
                this.attachATCListener();
                if (!this.form) return;
                this.render();
                this.atcObserver.disconnect();
                this.atcObserver = null;
              });

              this.atcObserver.observe(this.sectionRoot, {
                childList: true,
                subtree: true,
              });
            }
          }, 0);
        }

        disconnectedCallback() {
          if (this.form) {
            this.form.removeEventListener('submit', this.atcSubmitHandler, true);
          }
          if (this.atcObserver) {
            this.atcObserver.disconnect();
            this.atcObserver = null;
          }
        }

        refreshATCElements() {
          let atcScope = null;
          if (this.sectionRoot && this.sectionId) {
            atcScope = this.sectionRoot.querySelector(
              `.td-lpcp__atc-wrapper[data-lpcp-section-id="${this.sectionId}"]`
            );
          }
          if (!atcScope && this.sectionRoot) {
            atcScope = this.sectionRoot.querySelector('.td-lpcp__atc-wrapper');
          }

          this.atcScope = atcScope;
          this.form = atcScope ? atcScope.querySelector('.product-form form') : null;
          this.submitButton = atcScope ? atcScope.querySelector('.td-lpcp__atc-button') : null;
          this.atcPriceEl = atcScope ? atcScope.querySelector('[data-atc-price]') : null;
          this.atcCompareEl = atcScope ? atcScope.querySelector('[data-atc-compare-price]') : null;
          this.errorWrapper = atcScope ? atcScope.querySelector('.product-form__error-message-wrapper') : null;
          this.errorMessage = this.errorWrapper ? this.errorWrapper.querySelector('.product-form__error-message') : null;
        }

        attachATCListener() {
          if (!this.form) return;
          this.form.removeEventListener('submit', this.atcSubmitHandler, true);
          this.form.addEventListener('submit', this.atcSubmitHandler, true);
        }

        readJsonData(selector) {
          const el = this.querySelector(selector);
          if (!el) return [];
          try {
            return JSON.parse(el.textContent || '[]');
          } catch (error) {
            console.error('[td-card-buy-box] Failed parsing JSON data', error);
            return [];
          }
        }

        resolveMoneyFormatter() {
          const moneyFormat = window.theme?.settings?.money_with_currency_format || window.theme?.settings?.money_format;
          if (window.FoxTheme?.Currency?.formatMoney && moneyFormat) {
            return (cents) => window.FoxTheme.Currency.formatMoney(cents, moneyFormat);
          }
          return (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;
        }

        get mainOnetimePrice() {
          return Number(this.dataset.mainOnetimePrice || 0);
        }

        get mainOnetimeCompare() {
          return Number(this.dataset.mainOnetimeCompare || 0);
        }

        get mainSubscriptionBasePrice() {
          return Number(this.dataset.mainSubscriptionPrice || this.mainOnetimePrice);
        }

        get mainSubscriptionBaseCompare() {
          return Number(this.dataset.mainSubscriptionCompare || 0);
        }

        get bundleOnetimePrice() {
          return Number(this.dataset.bundleOnetimePrice || 0);
        }

        get bundleOnetimeCompare() {
          return Number(this.dataset.bundleOnetimeCompare || 0);
        }

        get hasBundle() {
          return this.dataset.bundleEnabled === 'true' && Boolean(this.dataset.bundleVariantId);
        }

        get hasSubscription() {
          return this.dataset.hasSubscription === 'true' && this.mainPlans.length > 0;
        }

        get twoPackDiscountPct() {
          const value = Number(this.dataset.twoPackDiscount || 0);
          return Math.max(0, Math.min(100, value));
        }

        get bundleDiscountPct() {
          const value = Number(this.dataset.bundleDiscount || 0);
          return Math.max(0, Math.min(100, value));
        }

        get useCompareAtSavings() {
          return this.dataset.useCompareAtSavings === 'true';
        }

        get hideSubscriptionForBundles() {
          return this.dataset.hideSubscriptionForBundles === 'true';
        }

        get isThemeEditor() {
          return Boolean(window.Shopify && window.Shopify.designMode);
        }

        get showCardComparePrice() {
          return this.dataset.showCardComparePrice === 'true';
        }

        get frequencyBadgeDisplay() {
          const value = String(this.dataset.frequencyBadgeDisplay || 'subscription');
          if (['none', 'onetime', 'subscription', 'both'].includes(value)) {
            return value;
          }
          return 'subscription';
        }

        get frequencyOptionBadgeDisplay() {
          const value = String(this.dataset.frequencyOptionBadgeDisplay || 'percentage');
          if (['percentage', 'money', 'none'].includes(value)) return value;
          return 'percentage';
        }

        getSelectedMainPlan() {
          if (!this.hasSubscription) return null;
          const selectedId = String(this.state.selectedPlanId || '');
          return this.mainPlans.find((plan) => String(plan.id) === selectedId) || this.mainPlans[0] || null;
        }

        getBundlePlan() {
          if (!this.hasBundle || !this.bundlePlans.length) return null;
          return this.bundlePlans[0] || null;
        }

        getUnitPricing() {
          const mainPlan = this.getSelectedMainPlan();
          let mainSubPrice = this.mainSubscriptionBasePrice;
          let mainSubCompare = this.mainOnetimeCompare > this.mainOnetimePrice
            ? this.mainOnetimeCompare
            : this.mainOnetimePrice;
          if (mainPlan) {
            mainSubPrice = Number(mainPlan.priceValue || this.mainSubscriptionBasePrice);
          }

          const matchedBundlePlan = this.getBundlePlan();
          let bundleSubPrice = this.bundleOnetimePrice;
          let bundleSubCompare = this.bundleOnetimeCompare > this.bundleOnetimePrice
            ? this.bundleOnetimeCompare
            : this.bundleOnetimePrice;
          if (matchedBundlePlan) {
            bundleSubPrice = Number(matchedBundlePlan.priceValue || this.bundleOnetimePrice);
          }

          return {
            mainPlan,
            matchedBundlePlan,
            main: {
              onetime: { price: this.mainOnetimePrice, compare: this.mainOnetimeCompare },
              subscription: { price: mainSubPrice, compare: mainSubCompare },
            },
            bundle: {
              onetime: { price: this.bundleOnetimePrice, compare: this.bundleOnetimeCompare },
              subscription: { price: bundleSubPrice, compare: bundleSubCompare },
            },
          };
        }

        getPackageTotals(packageType, purchaseTypeOverride = null) {
          const pricing = this.getUnitPricing();
          const purchaseType = purchaseTypeOverride === 'subscription' || purchaseTypeOverride === 'onetime'
            ? purchaseTypeOverride
            : (this.state.purchaseType === 'subscription' ? 'subscription' : 'onetime');
          const mainPrice = pricing.main[purchaseType].price;
          const mainCompare = pricing.main[purchaseType].compare;
          let totalPrice = 0;
          let totalCompare = 0;
          let manualDiscountBase = 0;
          let manualDiscountPct = 0;

          if (packageType === 'double') {
            const baseDouble = mainPrice * 2;
            totalPrice = baseDouble;
            totalCompare = mainCompare * 2;
            manualDiscountBase = baseDouble;
            manualDiscountPct = this.twoPackDiscountPct;
          } else if (packageType === 'bundle') {
            const bundlePrice = pricing.bundle[purchaseType].price || 0;
            const bundleCompare = pricing.bundle[purchaseType].compare || 0;
            totalPrice = mainPrice + bundlePrice;
            totalCompare = mainCompare + bundleCompare;

            const bundleBaseRegularTotal = pricing.main.onetime.price + (pricing.bundle.onetime.price || 0);
            const bundleDiscountPct = this.bundleDiscountPct;
            if (bundleDiscountPct > 0 && bundleBaseRegularTotal > 0) {
              const bundleDiscountAmount = Math.round((bundleBaseRegularTotal * bundleDiscountPct) / 100);
              const discountedRegularTotal = Math.max(0, bundleBaseRegularTotal - bundleDiscountAmount);

              if (purchaseType === 'onetime') {
                totalPrice = discountedRegularTotal;
              } else {
                const mainSubscriptionSavings = Math.max(
                  0,
                  pricing.main.onetime.price - pricing.main.subscription.price
                );
                const bundleSubscriptionSavings = Math.max(
                  0,
                  (pricing.bundle.onetime.price || 0) - (pricing.bundle.subscription.price || 0)
                );
                totalPrice = Math.max(
                  0,
                  discountedRegularTotal - mainSubscriptionSavings - bundleSubscriptionSavings
                );
              }

              const compareCandidate = totalPrice + bundleDiscountAmount;
              if (compareCandidate > totalCompare) {
                totalCompare = compareCandidate;
              }
            }
          } else {
            totalPrice = mainPrice;
            totalCompare = mainCompare;
          }

          if (manualDiscountPct > 0 && manualDiscountBase > 0) {
            const manualDiscountAmount = Math.round((manualDiscountBase * manualDiscountPct) / 100);
            totalPrice = Math.max(0, totalPrice - manualDiscountAmount);
            const compareCandidate = totalPrice + manualDiscountAmount;
            if (compareCandidate > totalCompare) {
              totalCompare = compareCandidate;
            }
          }

          if (!(totalCompare > totalPrice)) {
            totalCompare = 0;
          }

          return { totalPrice, totalCompare, pricing };
        }

        formatMoney(cents) {
          return this.currencyFormatter(Number(cents || 0));
        }

        setNoCompareState(priceEl, hasCompare) {
          if (!priceEl) return;
          priceEl.classList.toggle('is-no-compare', !hasCompare);
        }

        setError(message = '') {
          if (!this.errorWrapper || !this.errorMessage) return;
          const hasError = Boolean(message);
          this.errorWrapper.toggleAttribute('hidden', !hasError);
          this.errorMessage.textContent = message;
        }

        handlePackageChange(button) {
          if (button.disabled) return;
          this.state.selectedPackage = String(button.dataset.package || 'single');
          this.render();
        }

        handlePurchaseTypeChange(button) {
          const nextType = String(button.dataset.purchaseType || 'onetime');
          if (nextType === 'subscription' && !this.hasSubscription) return;
          this.state.purchaseType = nextType;
          this.render();
        }

        renderPackageState() {
          const shouldHideBundle = this.hideSubscriptionForBundles && this.state.purchaseType === 'subscription';
          const bundleButton = this.querySelector('[data-package="bundle"]');
          if (bundleButton) {
            bundleButton.classList.toggle('is-suppressed', shouldHideBundle);
            bundleButton.setAttribute('aria-hidden', shouldHideBundle ? 'true' : 'false');
            bundleButton.toggleAttribute('inert', shouldHideBundle);
            if (this.isThemeEditor) {
              bundleButton.hidden = shouldHideBundle;
            } else {
              bundleButton.hidden = false;
            }
          }

          this.packageButtons.forEach((button) => {
            const isActive = String(button.dataset.package) === this.state.selectedPackage;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-checked', isActive ? 'true' : 'false');
          });

          if (this.bundleDetailsEl) {
            this.bundleDetailsEl.classList.toggle('is-open', this.state.selectedPackage === 'bundle');
          }
        }

        renderPurchaseState() {
          this.purchaseButtons.forEach((button) => {
            const purchaseType = String(button.dataset.purchaseType);
            const isActive = purchaseType === this.state.purchaseType;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-checked', isActive ? 'true' : 'false');
          });

          if (this.subscriptionWrap) {
            const subscriptionActive = this.state.purchaseType === 'subscription' && this.hasSubscription;
            this.subscriptionWrap.classList.toggle('is-active', subscriptionActive);
            const shouldHideSubscriptionOption =
              this.hideSubscriptionForBundles && this.state.selectedPackage === 'bundle';
            this.subscriptionWrap.classList.toggle('is-suppressed', shouldHideSubscriptionOption);
            this.subscriptionWrap.setAttribute('aria-hidden', shouldHideSubscriptionOption ? 'true' : 'false');
            this.subscriptionWrap.toggleAttribute('inert', shouldHideSubscriptionOption);
            if (this.isThemeEditor) {
              this.subscriptionWrap.hidden = shouldHideSubscriptionOption;
            } else {
              this.subscriptionWrap.hidden = false;
            }
          }

          if (this.subscriptionDetails) {
            this.subscriptionDetails.classList.toggle('is-hidden', this.state.purchaseType !== 'subscription');
          }

          if (this.bundleSubscriptionTextEl) {
            const showBundleText = this.state.selectedPackage === 'bundle';
            this.bundleSubscriptionTextEl.hidden = !showBundleText;
          }
        }

        renderCardPrices() {
          const totalsByPackageType = {};
          const getTotals = (packageType) => {
            if (!totalsByPackageType[packageType]) {
              totalsByPackageType[packageType] = this.getPackageTotals(packageType);
            }
            return totalsByPackageType[packageType];
          };

          this.packagePriceEls.forEach((el) => {
            const packageType = String(el.dataset.packagePrice || 'single');
            const totals = getTotals(packageType);
            el.textContent = this.formatMoney(totals.totalPrice);
            this.setNoCompareState(el, totals.totalCompare > totals.totalPrice);
          });

          this.packageCompareEls.forEach((el) => {
            const packageType = String(el.dataset.packageCompare || 'single');
            const totals = getTotals(packageType);
            if (this.showCardComparePrice && totals.totalCompare > totals.totalPrice) {
              el.hidden = false;
              el.textContent = this.formatMoney(totals.totalCompare);
            } else {
              el.hidden = true;
              el.textContent = '';
            }
          });
        }

        renderFrequencyPrices() {
          const packageType = this.state.selectedPackage;
          const onetimeTotals = this.getPackageTotals(packageType, 'onetime');
          const subscriptionTotals = this.getPackageTotals(packageType, 'subscription');

          if (this.onetimePriceEl) this.onetimePriceEl.textContent = this.formatMoney(onetimeTotals.totalPrice);
          if (this.onetimeCompareEl) {
            if (onetimeTotals.totalCompare > onetimeTotals.totalPrice) {
              this.onetimeCompareEl.hidden = false;
              this.onetimeCompareEl.textContent = this.formatMoney(onetimeTotals.totalCompare);
            } else {
              this.onetimeCompareEl.hidden = true;
              this.onetimeCompareEl.textContent = '';
            }
          }
          this.setNoCompareState(this.onetimePriceEl, onetimeTotals.totalCompare > onetimeTotals.totalPrice);

          if (this.subscriptionPriceEl) this.subscriptionPriceEl.textContent = this.formatMoney(subscriptionTotals.totalPrice);
          if (this.subscriptionCompareEl) {
            if (subscriptionTotals.totalCompare > subscriptionTotals.totalPrice) {
              this.subscriptionCompareEl.hidden = false;
              this.subscriptionCompareEl.textContent = this.formatMoney(subscriptionTotals.totalCompare);
            } else {
              this.subscriptionCompareEl.hidden = true;
              this.subscriptionCompareEl.textContent = '';
            }
          }
          this.setNoCompareState(this.subscriptionPriceEl, subscriptionTotals.totalCompare > subscriptionTotals.totalPrice);

          const showOnetimeBadge =
            this.frequencyOptionBadgeDisplay !== 'none' &&
            (this.frequencyBadgeDisplay === 'onetime' || this.frequencyBadgeDisplay === 'both');
          const onetimeSavings = onetimeTotals.totalCompare - onetimeTotals.totalPrice;
          const onetimeSavingsBase = onetimeTotals.totalCompare;
          if (this.onetimeBadgeEl) {
            if (showOnetimeBadge && onetimeSavings > 0 && onetimeSavingsBase > 0) {
              this.onetimeBadgeEl.hidden = false;
              if (this.frequencyOptionBadgeDisplay === 'money') {
                this.onetimeBadgeEl.textContent = `${this.dataset.savingsBadgeLabel || 'Save'} ${this.formatMoney(onetimeSavings)}`;
              } else {
                const pct = Math.round((onetimeSavings * 100) / onetimeSavingsBase);
                this.onetimeBadgeEl.textContent = `${this.dataset.savingsBadgeLabel || 'Save'} ${pct}%`;
              }
            } else {
              this.onetimeBadgeEl.hidden = true;
              this.onetimeBadgeEl.textContent = '';
            }
          }

          const showSubscriptionBadge =
            this.frequencyOptionBadgeDisplay !== 'none' &&
            (this.frequencyBadgeDisplay === 'subscription' || this.frequencyBadgeDisplay === 'both');
          if (this.savingsBadgeEl) {
            let savings = onetimeTotals.totalPrice - subscriptionTotals.totalPrice;
            let savingsBase = onetimeTotals.totalPrice;

            if (this.useCompareAtSavings && subscriptionTotals.totalCompare > subscriptionTotals.totalPrice) {
              savings = subscriptionTotals.totalCompare - subscriptionTotals.totalPrice;
              savingsBase = subscriptionTotals.totalCompare;
            }

            if (showSubscriptionBadge && this.hasSubscription && savings > 0 && savingsBase > 0) {
              this.savingsBadgeEl.hidden = false;
              if (this.frequencyOptionBadgeDisplay === 'money') {
                this.savingsBadgeEl.textContent = `${this.dataset.savingsBadgeLabel || 'Save'} ${this.formatMoney(savings)}`;
              } else {
                const pct = Math.round((savings * 100) / savingsBase);
                this.savingsBadgeEl.textContent = `${this.dataset.savingsBadgeLabel || 'Save'} ${pct}%`;
              }
            } else {
              this.savingsBadgeEl.hidden = true;
              this.savingsBadgeEl.textContent = '';
            }
          }

          if (this.subscriptionWrap) {
            this.subscriptionWrap.classList.toggle('is-hidden', !this.hasSubscription);
          }

          const totals = this.getPackageTotals(packageType);
          if (this.atcPriceEl) this.atcPriceEl.textContent = this.formatMoney(totals.totalPrice);
          if (this.atcCompareEl) {
            if (totals.totalCompare > totals.totalPrice) {
              this.atcCompareEl.hidden = false;
              this.atcCompareEl.textContent = this.formatMoney(totals.totalCompare);
            } else {
              this.atcCompareEl.hidden = true;
              this.atcCompareEl.textContent = '';
            }
          }
          this.setNoCompareState(this.atcPriceEl, totals.totalCompare > totals.totalPrice);
        }

        render() {
          if (this.hideSubscriptionForBundles && this.state.selectedPackage === 'bundle') {
            this.state.purchaseType = 'onetime';
          }

          if (this.state.purchaseType === 'subscription' && !this.hasSubscription) {
            this.state.purchaseType = 'onetime';
          }
          this.renderPackageState();
          this.renderPurchaseState();
          this.renderCardPrices();
          this.renderFrequencyPrices();
        }

        buildLineItems() {
          const mainVariantId = Number(this.dataset.mainVariantId || 0);
          if (!mainVariantId) return [];

          const lineItems = [];
          const selectedMainPlan = this.getSelectedMainPlan();
          const useSubscription = this.state.purchaseType === 'subscription' && this.hasSubscription && selectedMainPlan;
          const bundleMatchedPlan = this.getBundlePlan();

          if (this.state.selectedPackage === 'double') {
            const mainLine = { id: mainVariantId, quantity: 2 };
            if (useSubscription) mainLine.selling_plan = Number(selectedMainPlan.id);
            lineItems.push(mainLine);
            return lineItems;
          }

          if (this.state.selectedPackage === 'bundle') {
            const mainLine = { id: mainVariantId, quantity: 1 };
            if (useSubscription) mainLine.selling_plan = Number(selectedMainPlan.id);
            lineItems.push(mainLine);

            if (this.hasBundle) {
              const bundleLine = { id: Number(this.dataset.bundleVariantId), quantity: 1 };
              if (useSubscription && bundleMatchedPlan) {
                bundleLine.selling_plan = Number(bundleMatchedPlan.id);
              }
              lineItems.push(bundleLine);
            }
            return lineItems;
          }

          const singleMainLine = { id: mainVariantId, quantity: 1 };
          if (useSubscription) singleMainLine.selling_plan = Number(selectedMainPlan.id);
          lineItems.push(singleMainLine);
          return lineItems;
        }

        getSectionsToRender() {
          const candidates = [
            { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
            { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
            { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' },
          ];
          return candidates.filter((section) => {
            if (section.id === 'cart-drawer-toggle') return document.querySelector(section.selector);
            return document.getElementById(section.id);
          });
        }

        getSectionInnerHTML(html, selector = '.shopify-section') {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const target = parsed.querySelector(selector);
          return target ? target.innerHTML : '';
        }

        renderContents(parsedState) {
          if (!parsedState?.sections) return;
          let cartReinited = false;
          this.getSectionsToRender().forEach((section) => {
            if (section.id === 'cart-drawer-toggle') {
              const bubbleHtml = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
              document.querySelectorAll(section.selector).forEach((element) => {
                element.innerHTML = bubbleHtml;
              });
              return;
            }

            const root = document.getElementById(section.id);
            if (!root) return;
            const replaceTarget = root.querySelector(section.selector) || root;
            replaceTarget.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            if (!cartReinited) {
              if (typeof CartDrawer !== 'undefined') {
                new CartDrawer();
              }
              if (typeof Cart !== 'undefined') {
                new Cart().renderContents(parsedState);
              }
              cartReinited = true;
            }
          });

          const cartDrawer = document.getElementById('Cart-Drawer');
          if (cartDrawer) {
            document.body.classList.add('open-cc');
            document.body.classList.add('open-cart');
            cartDrawer.classList.add('active');
            if (typeof dispatchCustomEvent === 'function') {
              dispatchCustomEvent('cart-drawer:open');
            }
          }
        }

        async handleSubmit(event) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setError('');
          if (!this.submitButton) return;

          const items = this.buildLineItems();
          if (!items.length) {
            this.setError('Unable to add this item right now.');
            return;
          }

          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('loading');

          try {
            const payload = {
              items,
              sections: this.getSectionsToRender().map((section) => section.section),
              sections_url: window.location.pathname,
            };
            const response = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify(payload),
            });
            const json = await response.json();
            if (!response.ok || json?.status) {
              const message = json?.description || json?.message || 'Unable to add this item right now.';
              this.setError(message);
              return;
            }

            this.renderContents(json);
            if (typeof dispatchCustomEvent === 'function') {
              dispatchCustomEvent('cart:item-added', {
                product: json.hasOwnProperty('items') ? json.items[0] : json,
              });
            }
          } catch (error) {
            console.error('[td-card-buy-box] Add to cart failed', error);
            this.setError('Unable to add this item right now.');
          } finally {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
          }
        }
      }
    );
  }
